import { useState, useCallback, SetStateAction, Dispatch } from 'react'
import { Message } from '../types'
import { useAINoteSearch } from '../../../../hooks/useAINoteSearch'
import { useStreamHandler } from './useStreamHandler'
import { createUserMessage, createAssistantMessage, createErrorMessage } from '../utils/messageHelpers'
import { sendChatRequest, getStreamReader } from '../utils/apiClient'

/**
 * Props for the useChatConversation hook.
 */
interface UseChatConversationProps {
  messages: Message[]
  setMessages: Dispatch<SetStateAction<Message[]>>
  getNoteContent: () => string | null
  selectedText: string | null
  setPendingEdit: (message: Message | undefined) => void
  setShowEditConfirmation: (show: boolean) => void
}

/**
 * Hook for managing the entire chat conversation flow with the AI.
 * Orchestrates message sending, streaming responses, tool calls, and edit proposals.
 */
export function useChatConversation({
  messages,
  setMessages,
  getNoteContent,
  selectedText,
  setPendingEdit,
  setShowEditConfirmation
}: UseChatConversationProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [webSearchStatus, setWebSearchStatus] = useState<{ active: boolean; query?: string; url?: string; domain?: string; title?: string; outputIndex?: number }>({ active: false })
  const { searchNotesLocal, notesData } = useAINoteSearch()

  // Search handler for when server requests a search
  const handleSearchRequest = async (id: string, query: string, limit: number, fullContent: boolean, assistantMessageId?: string) => {
    console.log('[useChatConversation] Handling search request:', { id, query, limit, fullContent })
    setIsSearching(true)
    
    // Use the local search function
    const results = searchNotesLocal(query, limit)
    
    // Extract referenced notes for tracking with the query context
    const newReferencedNotes = results.map(r => ({
      recordName: r.recordName,
      title: r.title,
      query: query  // Track which query found this note
    }))
    
    // Update the assistant message by APPENDING to existing referenced notes
    if (assistantMessageId && newReferencedNotes.length > 0) {
      setMessages(previous => 
        previous.map(message => {
          if (message.id === assistantMessageId) {
            // Append to existing referenced notes, deduplicating by recordName
            const existingNotes = message.referencedNotes || []
            const existingRecordNames = new Set(existingNotes.map(n => n.recordName))
            const uniqueNewNotes = newReferencedNotes.filter(n => !existingRecordNames.has(n.recordName))
            return { 
              ...message, 
              referencedNotes: [...existingNotes, ...uniqueNewNotes]
            }
          }
          return message
        })
      )
    }
    
    // Format results for AI - include more content or full content based on parameter
    const formattedResults = results.map(r => {
      // If fullContent is requested, try to get the full note content
      let content = ''
      let foundFullContent = false
      
      if (fullContent && notesData) {
        // Search through all note maps to find the full content
        const allNotes = [
          ...(notesData.privateProjectNotes || []),
          ...(notesData.privateCalendarNotes || []),
          ...(notesData.teamProjectNotes || []),
          ...(notesData.teamCalendarNotes || [])
        ]
        const noteEntry = allNotes.find(([_, note]) => note.recordName === r.recordName)
        if (noteEntry && noteEntry[1].content) {
          content = noteEntry[1].content
          foundFullContent = true
        }
      }
      
      // If we don't have full content or it wasn't requested, use excerpts
      if (!content) {
        content = r.matchedLines.slice(0, 5).map(l => l.content).join('\n')
      }
      
      return {
        recordName: r.recordName,
        title: r.title,
        excerpt: content,  // Keep the field name as 'excerpt' for backward compatibility
        matchCount: r.matchedLines.length,
        isFullContent: foundFullContent
      }
    })
    
    const summary = results.length > 0
      ? `Found ${results.length} notes matching "${query}":\n${results.map((r, i) => `${i + 1}. ${r.title}`).join('\n')}`
      : `No notes found matching "${query}"`
    
    setIsSearching(false)
    return { results: formattedResults, summary }
  }

  // Handle search completion (kept for compatibility but no longer storing in state)
  const handleSearchComplete = useCallback((searches: Array<{ id: string; query: string; results: any[]; summary: string }>) => {
    console.log('[useChatConversation] Searches completed:', searches.length)
  }, [])

  // Use the stream handler hook
  const { processStream, handleEditProposal, handleEmptyResponse } = useStreamHandler({
    setMessages,
    setPendingEdit,
    setShowEditConfirmation,
    setWebSearchStatus,
    onSearchRequest: handleSearchRequest,
    onSearchComplete: handleSearchComplete
  })

  /**
   * Sends a message to the AI chat API and handles the streaming response.
   */
  const sendMessage = useCallback(async (content: string, images?: string[]) => {
    console.log('[useChatConversation] Sending message:', content, 'with images:', images?.length ?? 0)
    
    // Create and add user message
    const userMessage = createUserMessage(content, images)
    setMessages(previous => [...previous, userMessage])
    setIsLoading(true)

    // Create and add assistant message placeholder
    const assistantMessage = createAssistantMessage()
    const assistantMessageId = assistantMessage.id
    setMessages(previous => [...previous, assistantMessage])

    // Get current note content
    const noteContent = getNoteContent()

    try {
      // Send the chat request
      const response = await sendChatRequest({
        messages: [...messages, userMessage],
        noteContent,
        selectedText
      })

      // Get the stream reader
      const reader = getStreamReader(response)

      // Process the stream
      const { accumulatedContent, toolCalls, searches } = await processStream(reader, assistantMessageId)

      // If we collected search context, send it to the server for final processing
      if (searches.length > 0 && accumulatedContent.length === 0) {
        console.log('[useChatConversation] Sending collected search context to server:', searches.length, 'searches')
        console.log('[useChatConversation] Search details:', searches.map(s => ({ query: s.query, resultCount: s.results.length })))
        
        try {
          const contextResponse = await fetch('/api/chat/process-search-results', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              searches: searches,
              messages: [...messages, userMessage],
              noteContent,
              selectedText
            })
          })
          
          console.log('[useChatConversation] Context response status:', contextResponse.status)
          
          if (contextResponse.ok) {
            const finalResponse = await contextResponse.json()
            console.log('[useChatConversation] Final response received:', { 
              hasContent: !!finalResponse.content, 
              contentLength: finalResponse.content?.length,
              searchCount: finalResponse.searchCount 
            })
            
            if (finalResponse.content) {
              setMessages(previous => 
                previous.map(message => 
                  message.id === assistantMessageId 
                    ? { ...message, content: finalResponse.content }
                    : message
                )
              )
              console.log('[useChatConversation] Updated assistant message with content')
            } else {
              console.log('[useChatConversation] No content in final response')
            }
          } else {
            const errorText = await contextResponse.text()
            console.error('[useChatConversation] Context response error:', contextResponse.status, errorText)
          }
        } catch (error) {
          console.error('[useChatConversation] Error sending context to server:', error)
        }
        
        return
      }

      // Handle empty response
      if (accumulatedContent.length === 0) {
        handleEmptyResponse(assistantMessageId)
        return
      }

      // Tool calls are now handled differently (search happens client-side)
      // This loop is kept for potential future tool implementations
      for (const { toolCall, toolResult } of toolCalls) {
        console.log('[useChatConversation] Tool call received (legacy):', toolCall)
        // Currently not used as search is handled client-side
      }

      // Check for and handle edit proposals
      handleEditProposal(accumulatedContent, noteContent, assistantMessageId)

    } catch (error) {
      console.error('[useChatConversation] Error sending message:', error)
      
      // Update the assistant message with error
      const errorMsg = createErrorMessage(error, assistantMessageId)
      setMessages(previous => 
        previous.map(message => 
          message.id === assistantMessageId 
            ? { ...message, ...errorMsg }
            : message
        )
      )
    } finally {
      setIsLoading(false)
    }
  }, [messages, getNoteContent, selectedText, setMessages, processStream, handleEditProposal, handleEmptyResponse, searchNotesLocal])

  /**
   * Clears all messages from the chat conversation.
   */
  const clearMessages = useCallback(() => {
    setMessages([])
  }, [setMessages])

  return {
    sendMessage,
    clearMessages,
    isLoading,
    isSearching,
    webSearchStatus
  }
}